import type { BoardLightCommand } from '../types/device';

/**
 * Lighting a Moonboard or a Kilter from the browser, on a wall with its LED kit
 * (Board#locally_lightable?).
 *
 * A box of ours on the wall would do exactly this over MQTT: connect to the
 * board and write the frame the server encoded, byte for byte (see
 * BoardLightPayload). There is no protocol knowledge here either — this is the
 * same relay, running in the page. The one thing it has to know is how the
 * frame travels: a Moonboard's is text, a Kilter's is the Aurora packets as hex
 * (`boardType` says which).
 *
 * The transport is the boards' own: a Nordic UART Service, written 20 bytes at a
 * time. The UUIDs and the chunking are the firmware's (`bleWrite` in the device
 * repo, docs/mqtt-board-commands.md) and have to stay in step with it, since the
 * two write the same frames to the same walls.
 *
 * Web Bluetooth is not everywhere: Chrome and Edge have it on Android, macOS,
 * Windows and ChromeOS, Safari and Firefox have none, and it needs a secure
 * context. `bluetoothAvailable` is what the UI asks before offering any of this.
 */

/** The service every Moonboard and Kilter exposes to be written to. */
export const NUS_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';

/** Its RX characteristic: what the board reads, and all we ever touch. */
export const NUS_RX_CHARACTERISTIC = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

/**
 * With the default MTU of 23 bytes a BLE packet carries 20 of payload. The same
 * granularity the official apps use, so there is no MTU to renegotiate.
 */
const CHUNK_BYTES = 20;

/** Without an acknowledgement per chunk, the board needs a moment between them. */
const CHUNK_PAUSE_MS = 15;

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A Kilter frame as the server sends it (hex) back into the bytes it is. */
export function hexBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(Math.floor(hex.length / 2));
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

/** Whether this browser has Web Bluetooth at all. */
export function bluetoothSupported(): boolean {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
}

/**
 * Whether it is usable here and now: the API is present *and* the machine has a
 * radio to use. A desktop with Bluetooth switched off answers false, which is
 * the honest answer — the button would only ever open an empty chooser.
 */
export async function bluetoothAvailable(): Promise<boolean> {
  if (!bluetoothSupported()) return false;

  try {
    return await navigator.bluetooth.getAvailability();
  } catch {
    // An older implementation without `getAvailability` still has the chooser.
    return true;
  }
}

/**
 * The climber closed the chooser without picking anything. Not a failure worth
 * a message: they changed their mind, and the page carries on as it was.
 */
export function chooserDismissed(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotFoundError';
}

/**
 * One board, one link, for as long as the page is on it.
 *
 * Writes are serialised: the Web Bluetooth stack refuses a second GATT
 * operation while one is in flight, and a circuit's flashing would otherwise
 * write over the frame the climber is looking at.
 */
export class BoardBluetoothLink {
  private device: BluetoothDevice | null = null;
  private rx: BluetoothRemoteGATTCharacteristic | null = null;
  /** The tail of the write queue — every operation waits for it. */
  private queue: Promise<unknown> = Promise.resolve();
  /**
   * Which frame the wall is showing. Bumped by every new command, which is how a
   * flashing cycle from the previous one learns it is over: the step after this
   * one must never end up alternating against it.
   */
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private waiting: ((alive: boolean) => void) | null = null;
  private lost: (() => void) | null = null;

  get connected(): boolean {
    return !!this.device?.gatt?.connected && !!this.rx;
  }

  /** What the board is called, once one has been picked. */
  get name(): string | null {
    return this.device?.name ?? null;
  }

  /**
   * Opens the chooser and connects to what the climber picks.
   *
   * Must be called straight out of a click: the browser only opens the chooser
   * inside a user gesture, and an `await` before it loses that.
   *
   * Everything around is listed, unfiltered, because there is nothing here that
   * could be filtered on honestly. A board's `bluetooth_name` is a label a gym
   * typed — "Vertigini", the name of the wall — and not necessarily what the
   * controller announces over the air ("MoonBoard A"); `namePrefix` matches
   * exactly, case included. A filter built on that shows a chooser that scans
   * for ever and lists nothing, which is far worse than a long list: the names
   * are in it, and the climber can pick their own wall out of them.
   *
   * The box on the wall does the same thing for the same reason: it reports
   * every named advertisement it hears and leaves the choosing to a person (see
   * `BtScanCallbacks` in the firmware).
   */
  async connect(): Promise<string | null> {
    // Whatever was held before this: a board picked twice, or one that dropped
    // us and is being picked again.
    this.disconnect();

    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      // Not a filter: permission to reach that service once connected.
      optionalServices: [NUS_SERVICE],
    });

    try {
      const server = await device.gatt?.connect();
      if (!server) throw new Error('The board refused the connection');

      const service = await server.getPrimaryService(NUS_SERVICE);
      this.rx = await service.getCharacteristic(NUS_RX_CHARACTERISTIC);
    } catch (error) {
      // Connected but unusable — a board left holding an open link nobody knows
      // about would refuse the next attempt as well.
      device.gatt?.disconnect();
      throw error;
    }

    this.device = device;
    device.addEventListener('gattserverdisconnected', this.handleDisconnected);

    return device.name ?? null;
  }

  /** Called when the link drops on its own — the board went, or took another phone. */
  onLost(listener: (() => void) | null): void {
    this.lost = listener;
  }

  /**
   * Puts a command on the wall: the still frame at once, and then — if the
   * command carries one — the flashing that says where the circuit is heading.
   *
   * Resolves as soon as the first frame is written. The alternation runs behind
   * it for as long as nothing else is asked of the board, exactly as the
   * firmware does it: the next change is timed from the end of the last write,
   * so frames never pile up and a slow board simply pulses slower.
   */
  async show(command: BoardLightCommand): Promise<void> {
    const { holds, blink, boardType } = command.data;
    const generation = this.begin();
    const encode = boardType === 'Kilter' ? hexBytes : (frame: string) => new TextEncoder().encode(frame);

    await this.write(encode(holds));

    if (blink) void this.pulse(generation, encode(holds), { ...blink, holds: encode(blink.holds) });
  }

  /**
   * Stops the flashing and leaves the wall as it is. The problem stays lit —
   * the same thing the device transport does when it hands a board back.
   */
  stop(): void {
    this.begin();
  }

  /**
   * Gives the board back, so the next phone can have it. The link can be used
   * again afterwards — connecting picks a board, this only lets one go.
   */
  disconnect(): void {
    this.stop();

    const device = this.device;
    this.device = null;
    this.rx = null;
    if (!device) return;

    device.removeEventListener('gattserverdisconnected', this.handleDisconnected);
    if (device.gatt?.connected) device.gatt.disconnect();
  }

  private handleDisconnected = () => {
    this.begin();
    this.rx = null;
    this.lost?.();
  };

  /**
   * Starts a new command: cancels the cycle in flight and hands back the tag the
   * new one runs under.
   */
  private begin(): number {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.waiting?.(false);
    this.waiting = null;

    return ++this.generation;
  }

  private async pulse(
    generation: number,
    still: Uint8Array,
    blink: { holds: Uint8Array; onMs: number; offMs: number }
  ): Promise<void> {
    // The still frame is already on the wall — `show` wrote it before us.
    let showingStill = true;

    while (this.generation === generation && this.connected) {
      const alive = await this.wait(showingStill ? blink.onMs : blink.offMs, generation);
      if (!alive) return;

      showingStill = !showingStill;
      try {
        await this.write(showingStill ? still : blink.holds);
      } catch {
        // The wall stopped answering. The run on screen is worth keeping, and
        // the climber can see for themselves that the LEDs have gone.
        return;
      }
    }
  }

  /** Sleeps, unless the command is replaced first — then it resolves false. */
  private wait(ms: number, generation: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.waiting = resolve;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.waiting = null;
        resolve(this.generation === generation);
      }, ms);
    });
  }

  /** One frame, in packets the board's MTU can carry. */
  private write(bytes: Uint8Array): Promise<void> {
    return this.enqueue(async () => {
      const rx = this.rx;
      if (!rx || !this.connected) throw new Error('The board is not connected');

      // With an acknowledgement per packet the board sets the pace itself; only
      // without one do we have to guess at it. Same choice the firmware makes.
      const withResponse = rx.properties?.write !== false;

      for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
        const chunk = bytes.slice(offset, offset + CHUNK_BYTES);

        if (withResponse) {
          await (rx.writeValueWithResponse?.(chunk) ?? rx.writeValue(chunk));
        } else {
          await (rx.writeValueWithoutResponse?.(chunk) ?? rx.writeValue(chunk));
          await pause(CHUNK_PAUSE_MS);
        }
      }
    });
  }

  /**
   * Runs after whatever is already going out, failed or not: one board, one
   * radio, one operation at a time.
   */
  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const run = this.queue.then(operation, operation);
    this.queue = run.catch(() => undefined);

    return run;
  }
}
